import { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  cardRideIdsSelector,
  kanbanRidesToDisplaySelector,
  OccupancyEnum,
  resolveSelected,
  ScrollAreaEnum,
  selectedTableRowsIdsAtomFamily,
  selectedTableRowsIdsSetSF,
} from "@ryde/shared";
import { orangeMui } from "@ryde/theme";

import { RidesRoutes } from "../../routes";
import { KanbanColumn, KanbanColumnHeader } from "./item";
import { RidesKanbanMap } from "./rides.kanban.map";
import { KanbanWrapperStyled } from "./rides.kanban.styled";

export interface IGridCell {
  $gridArea: string;
  $borderBottomColor?: string;
}

export const KanbanWrapper: FC = () => {
  const lowOccupancyRows = useRecoilValue(
    kanbanRidesToDisplaySelector(OccupancyEnum.Low),
  );
  const mediumOccupancyRows = useRecoilValue(
    kanbanRidesToDisplaySelector(OccupancyEnum.Medium),
  );
  const highOccupancyRows = useRecoilValue(
    kanbanRidesToDisplaySelector(OccupancyEnum.High),
  );
  const cardRideIds = useRecoilValue(cardRideIdsSelector);
  const setSelectedTableRowsIds = useSetRecoilState(
    selectedTableRowsIdsAtomFamily("ride"),
  );
  const selectedTableRowsIdsSet = useRecoilValue(
    selectedTableRowsIdsSetSF("ride"),
  );
  const allRows = useMemo(
    () => [...lowOccupancyRows, ...mediumOccupancyRows, ...highOccupancyRows],
    [highOccupancyRows, lowOccupancyRows, mediumOccupancyRows],
  );

  useEffect(() => {
    resolveSelected({
      selectedTableRowsIds: selectedTableRowsIdsSet,
      setSelectedTableRowsIds,
      tableIds: cardRideIds,
    });
    // We want an effect only on ids grid ids changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardRideIds]);

  const { t } = useTranslation();
  return (
    <>
      <KanbanWrapperStyled>
        <KanbanColumnHeader
          gridArea="head1"
          itemsCount={lowOccupancyRows?.length}
          title={t("ride.OCCUPANCY.LOW")}
          bottomBorderColor={"red"}
        />

        <KanbanColumnHeader
          gridArea="head2"
          itemsCount={mediumOccupancyRows?.length}
          title={t("ride.OCCUPANCY.MEDIUM")}
          bottomBorderColor={orangeMui.A400}
        />

        <KanbanColumnHeader
          gridArea="head3"
          itemsCount={highOccupancyRows?.length}
          title={t("ride.OCCUPANCY.HIGH")}
          bottomBorderColor={"green"}
        />

        <KanbanColumn
          gridArea="col1"
          items={lowOccupancyRows}
          scrollArea={ScrollAreaEnum.RidesKanbanColumnLow}
        />
        <KanbanColumn
          gridArea="col2"
          items={mediumOccupancyRows}
          scrollArea={ScrollAreaEnum.RidesKanbanColumnMedium}
        />
        <KanbanColumn
          gridArea="col3"
          items={highOccupancyRows}
          scrollArea={ScrollAreaEnum.RidesKanbanColumnHigh}
        />
        <RidesKanbanMap $gridArea={"map"} />
      </KanbanWrapperStyled>
      <RidesRoutes />
    </>
  );
};
